import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";

import AlertDialog from "components/AlertDialog";
import BasicDialog from "components/BasicDialog";
import MDTypography from "components/MDTypography";
import { useLanguage } from "contexts/language.context";
import { useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import auditsService from "services/audits-service";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function Attachments({
  getAudit,
  attachments,
  setAttachments,
  readOnly,
}) {
  const [currentFile, setCurrentFile] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { label } = useLanguage();
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState({
    content: "",
    title: "",
    onOk: () => { },
    onCancel: () => { },
  });
  const [loading, setLoading] = useState(false);

  function handleContactRemove(attachmentIndex) {
    setOpen(true);
    setDialog({
      content: "Are you sure you want to delete this attachment?",
      title: "Delete attachment?",
      onOk: () => {
        const newAttachments = [...attachments];
        newAttachments.splice(attachmentIndex, 1);
        setAttachments(newAttachments);
      },
      onCancel: () => { },
    });
  }

  async function handleUpdateAttachment(attachmentId, updates) {
    setLoading(true);
    const [response, data] = await auditsService.updateAttachement(
      1,
      1,
      attachmentId,
      updates,
    );

    if (response.ok) {
      await getAudit();
      setDialogOpen(false);
    }

    setLoading(false);
  }

  return (
    <>
      <BasicDialog
        maxWidth={"md"}
        fullWidth={true}
        title={label("attachment-singular")}
        content={
          <Box
            sx={{
              py: 1,
              display: "flex",
              flexDirection: "row",
              flexWrap: { xs: "wrap", sm: "nowrap" },
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", sm: "auto" },
                flexShrink: 0,
              }}
            >
              {currentFile?.type?.startsWith("audio") ? (
                <audio
                  controls
                  src={currentFile?.url}
                />
              ) : (
                <img
                  style={{
                    maxHeight: "700px",
                    maxWidth: "100%",
                    width: "auto",
                    objectFit: "contain",
                    borderRadius: "8px",
                  }}
                  src={currentFile?.url}
                />
              )}
            </Box>

            <TextField
              label={label("remark-plural")}
              multiline
              minRows={1}
              fullWidth
              sx={{
                flex: 1,
                width: { xs: "100%", sm: "auto" },
                minWidth: { xs: "100%", sm: "220px" },
                ":disabled": {
                  color: "black !important",
                  backgroundColor: "black !important",
                },
              }}
              value={currentFile?.remarks || ""}
              onChange={(e) => {
                if (readOnly) return;
                setCurrentFile({ ...currentFile, remarks: e.target.value });
              }}
            />
          </Box>
        }
        actions={
          readOnly
            ? null
            : [
                {
                  text: label("save-button"),
                  onClick: () => {
                    handleUpdateAttachment(currentFile.id, {
                      remarks: currentFile.remarks,
                    });
                  },
                  props: {
                    variant: "gradient",
                    color: "info",
                    loading,
                  },
                },
              ]
        }
        cancelButton={true}
        open={dialogOpen}
        setOpen={setDialogOpen}
      />
      <AlertDialog open={open} setOpen={setOpen} {...dialog} />
      <Box
        display="flex"
        gap={1}
        style={{
          minWidth: "350px",
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
        }}
      >
        {attachments?.length > 0 &&
          attachments.map((file, index) => {
            return (
              <Box
                key={`Box: ${file?.name + file?.url + index}`}
                sx={{
                  p: 0.5,
                  backgroundColor: "white",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "16px",
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  cursor: "pointer",
                }}
                onClick={() => {
                  setCurrentFile(file);
                  setDialogOpen(true);
                }}
              >
                <Card
                  sx={{
                    height: "70px",
                    overflow: "hidden",
                    width: "70px",
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    {!readOnly && (
                      <IconButton
                        sx={{
                          position: "absolute",
                          right: "3px",
                          top: "3px",
                          color: "white !important",
                          cursor: "pointer",
                          zIndex: 1,
                          padding: 0.25,
                          backgroundColor: "secondary.main",

                          ":hover": {
                            backgroundColor: "secondary.focus",
                          },

                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                        color="secondary"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleContactRemove(index);
                        }}
                      >
                        <Icon>close</Icon>
                      </IconButton>
                    )}
                    {file.type?.startsWith("audio") ? (
                      <Icon fontSize="large">audiotrack</Icon>
                    ) : (
                      <img
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          objectPosition: "center",
                        }}
                        src={file.url}
                      />
                    )}
                  </Box>
                </Card>
                {file.remarks ? (
                  <Typography
                    sx={{
                      maxWidth: "150px",
                      fontSize: "14px",
                      textOverflow: "wrap",
                      whiteSpace: "wrap",
                      textAlign: "start",
                    }}
                  >
                    {file.remarks || ""}
                  </Typography>
                ) : null}
              </Box>
            );
          })}
        {!readOnly && (
          <Box sx={{ p: 1 }}>
            <Card sx={{ overflow: "hidden" }} key={`add`}>
              <Box
                height="60px"
                width="60px"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <IconButton size="large" role={undefined} component="label">
                  <Icon>add</Icon>
                  <VisuallyHiddenInput
                    type="file"
                    accept="image/*,audio/*"
                    onChange={(e) => {
                      if (!e.target.files[0]) return;
                      const url = URL.createObjectURL(e.target.files[0]);
                      setAttachments([
                        ...attachments,
                        {
                          name: e.target.files[0].name,
                          url: url,
                          file: e.target.files[0],
                          type: e.target.files[0].type,
                        },
                      ]);
                    }}
                  />
                </IconButton>
              </Box>
            </Card>
          </Box>
        )}
      </Box>
    </>
  );
}

function _Attachments({ attachments, setAttachments }) {
  const [currentFile, setCurrentFile] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <BasicDialog
        title={"Attachment"}
        content={
          currentFile?.type?.startsWith("audio/") ? (
            <audio controls src={currentFile?.url} style={{ width: "100%" }} />
          ) : (
            <img
              style={{
                width: "100%",
              }}
              src={currentFile?.url}
            />
          )
        }
        cancelButton={true}
        open={dialogOpen}
        setOpen={setDialogOpen}
      />
      <Card>
        <Box p={1} display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row">
            <MDTypography variant="h6" fontWeight="regular">
              Attachments
            </MDTypography>
            <IconButton size="small" role={undefined} component="label">
              <Icon>add</Icon>
              <VisuallyHiddenInput
                type="file"
                accept="image/*,audio/*"
                onChange={(e) => {
                  if (!e.target.files[0]) return;
                  const url = URL.createObjectURL(e.target.files[0]);
                  setAttachments([
                    ...attachments,
                    {
                      name: e.target.files[0].name,
                      url: url,
                      file: e.target.files[0],
                      type: e.target.files[0].type,
                    },
                  ]);
                }}
              />
            </IconButton>
          </Box>
          <Box display="flex" gap={1}>
            {attachments?.length > 0 ? (
              attachments.map((file, index) => (
                <Card
                  sx={{ overflow: "hidden" }}
                  key={`File: ${file?.name || file?.url || index}`}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    pl={1}
                    sx={{ textOverflow: "ellipsis" }}
                  >
                    <MDTypography
                      sx={{
                        width: 20,
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                      variant="inherit"
                      color="secondary"
                    >
                      {""}
                    </MDTypography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const newAttachments = [...attachments];
                        newAttachments.splice(index, 1);
                        setAttachments(newAttachments);
                      }}
                    >
                      <Icon>close</Icon>
                    </IconButton>
                  </Box>

                  <Box
                    height="50px"
                    width="60px"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    onClick={() => {
                      setCurrentFile(file);
                      setDialogOpen(true);
                    }}
                  >
                    {file.type?.startsWith("audio/") ? (
                      <Icon>audiotrack</Icon>
                    ) : (
                      <img
                        style={{
                          width: "100%",
                          maxHeight: "100px",
                          maxWidth: "100px",
                        }}
                        src={file.url}
                      />
                    )}
                  </Box>
                </Card>
              ))
            ) : (
              <MDTypography variant="inherit" color="secondary">
                No attachments uploaded
              </MDTypography>
            )}
          </Box>
        </Box>
      </Card>
    </>
  );
}
